import React from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Printer, Download, ShieldCheck, ArrowLeft } from "lucide-react";
import Layout from "../components/Layout";

/**
 * EFL — Electricity Facts Label (Texas PUCT regulatory document).
 *
 * Renders a print-friendly facts page per plan. Uses window.print() for
 * export-to-PDF (browsers convert print -> PDF natively, no backend needed).
 *
 * Sections: Pricing, Disclosures (TOS link), Renewable content, YRAC link.
 *
 * Plan data is locally defined to keep it deterministic; when the plan
 * catalog moves to the backend this component reads it from API.
 */
const PLANS = {
  flexible: {
    name: "Velcor Flexible",
    term: "12 months",
    type: "Tarifa fija (Fixed rate)",
    energyCharge: "11.9¢ / kWh",
    baseCharge: "$0 / month",
    tdu: "Pass-through (per utility)",
    minUsageFee: "$0",
    cancellationFee: "$0",
    renewable: "100%",
    avgPrice500: "13.4¢",
    avgPrice1000: "12.6¢",
    avgPrice2000: "12.2¢",
  },
  saver: {
    name: "Velcor Saver",
    term: "24 months",
    type: "Tarifa fija (Fixed rate)",
    energyCharge: "10.9¢ / kWh",
    baseCharge: "$0 / month",
    tdu: "Pass-through (per utility)",
    minUsageFee: "$0",
    cancellationFee: "$0",
    renewable: "100%",
    avgPrice500: "12.4¢",
    avgPrice1000: "11.6¢",
    avgPrice2000: "11.2¢",
  },
};

export default function EFL() {
  const { t } = useTranslation();
  const { planId = "flexible" } = useParams();
  const plan = PLANS[planId] || PLANS.flexible;
  const navigate = useNavigate();
  // Use browser history (navigate(-1)) so portal customers viewing an EFL
  // from their document vault return to /portal/documents, while marketing
  // visitors return to /shop or wherever they came from. Falls back to /shop
  // when there's nothing in history (direct link).
  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate("/shop"));
  const [searchParams] = useSearchParams();
  const fromPortal = searchParams.get("from") === "portal";

  return (
    <Layout minimal={fromPortal}>
      <Helmet>
        <title>EFL — {plan.name} — Velcor Energy</title>
        <meta name="description" content={`Electricity Facts Label for the ${plan.name} plan.`} />
      </Helmet>

      {/* Toolbar (hidden on print) */}
      <div className="bg-slate-50 border-b border-slate-200 print:hidden">
        <div className="max-w-[820px] mx-auto px-5 py-4 flex items-center justify-between gap-3">
          <button onClick={goBack} type="button" className="inline-flex items-center gap-1.5 text-[13px] font-extrabold text-slate-600 hover:text-velcor-green transition-colors" data-testid="efl-back">
            <ArrowLeft className="w-3.5 h-3.5" />
            {t("eflPage.back")}
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-200 hover:border-velcor-green hover:text-velcor-green text-[12.5px] font-extrabold transition-colors"
              data-testid="efl-print"
            >
              <Printer className="w-3.5 h-3.5" />
              {t("eflPage.print")}
            </button>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-velcor-green text-white text-[12.5px] font-extrabold hover:bg-velcor-green/90"
              data-testid="efl-pdf"
            >
              <Download className="w-3.5 h-3.5" />
              {t("eflPage.savePdf")}
            </button>
          </div>
        </div>
      </div>

      {/* The document — designed to look like a real EFL */}
      <article
        className="max-w-[820px] mx-auto px-6 py-10 md:py-14 bg-white"
        data-testid="efl-document"
        style={{ fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif" }}
      >
        {/* Header */}
        <header className="border-b-2 border-velcor-ink pb-5 mb-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-[10.5px] font-extrabold uppercase tracking-[0.18em] text-velcor-green mb-1">
                Texas PUCT Regulatory Document
              </div>
              <h1 className="font-black text-[26px] md:text-[32px] text-velcor-ink leading-tight tracking-tight">
                Electricity Facts Label
              </h1>
              <div className="text-slate-500 text-[13.5px] mt-1">
                {plan.name} · {plan.type} · {plan.term}
              </div>
            </div>
            <img src="/velcor-logo.png" alt="Velcor Energy" width={56} height={56} style={{ width: 56, height: 56 }} className="object-contain print:opacity-100" />
          </div>
        </header>

        {/* Pricing */}
        <Section title="1. Electricity Price">
          <Table
            rows={[
              ["Average Price per kWh @ 500 kWh", plan.avgPrice500],
              ["Average Price per kWh @ 1,000 kWh", plan.avgPrice1000],
              ["Average Price per kWh @ 2,000 kWh", plan.avgPrice2000],
              ["Energy Charge", plan.energyCharge],
              ["Base Charge", plan.baseCharge],
              ["TDU Delivery Charges", plan.tdu],
              ["Minimum Usage Fee", plan.minUsageFee],
            ]}
          />
          <p className="text-[12px] text-slate-500 mt-3 italic leading-relaxed">
            The average prices shown above include the energy charge, base charge, and TDU delivery charges,
            but do not include the impact of any usage credits or rebates.
          </p>
        </Section>

        {/* Disclosures */}
        <Section title="2. Other Key Terms and Questions">
          <Table
            rows={[
              ["Type of Product", plan.type],
              ["Contract Term", plan.term],
              ["Do I have a termination fee or any fees associated with terminating service?", "$0 — cancel any time without penalty"],
              ["Can my price change during contract period?", "Only as specified in the Terms of Service (TOS). Pass-through TDU charges may change."],
              ["What other fees may I be charged?", "Late payment fees, returned-payment fees, disconnect/reconnect fees per the TOS."],
              ["Is this a pre-pay or pay-in-advance product?", "No"],
            ]}
          />
        </Section>

        {/* Renewable */}
        <Section title="3. Renewable Content">
          <Table
            rows={[
              ["Renewable Content of this Product", plan.renewable],
              ["Statewide Average for Renewable Content", "Approx. 21% (per ERCOT)"],
            ]}
          />
        </Section>

        {/* YRAC anchor */}
        <Section title="4. Your Rights as a Customer (YRAC)" anchor="yrac">
          <p className="text-[13.5px] text-slate-700 leading-relaxed">
            For a complete description of customer rights, including your right to dispute, switch providers,
            access your usage history, and protections against discrimination and disconnection, please refer to the
            "Your Rights as a Customer" document, available on request from Velcor Energy and on file with the Public
            Utility Commission of Texas (PUCT).
          </p>
        </Section>

        {/* TOS anchor */}
        <Section title="5. Terms of Service (TOS)" anchor="tos">
          <p className="text-[13.5px] text-slate-700 leading-relaxed">
            The full Terms of Service governing this plan are available on request and at velcorenergy.com/terms.
            By enrolling, the customer agrees to all provisions of the TOS, including but not limited to billing,
            payment, late fees, contract renewal, early termination, and dispute resolution.
          </p>
        </Section>

        {/* Footer */}
        <footer className="mt-10 pt-5 border-t border-slate-200 grid grid-cols-1 md:grid-cols-2 gap-4 text-[12px] text-slate-500">
          <div>
            <div className="font-black text-velcor-ink text-[13px] mb-1">Velcor Energy LLC</div>
            440 Louisiana St, Ste 1200<br />
            Houston, TX 77002-1063<br />
            (800) 715-6516 · support@velcorenergy.com
          </div>
          <div className="md:text-right">
            <span className="inline-flex items-center gap-1.5 text-velcor-green font-extrabold">
              <ShieldCheck className="w-3.5 h-3.5" /> PUCT REP# Pending
            </span>
            <div className="mt-1">Effective: {new Date().toLocaleDateString("en-US")}</div>
          </div>
        </footer>
      </article>

      {/* Print styles */}
      <style>{`
        @media print {
          body { background: white; }
          [data-testid="footer"], [data-testid="topbar"], [data-testid="topribbon"], [data-testid="bottomnav"], [data-testid="whatsapp-float"], [data-testid="sticky-zip-bar"] { display: none !important; }
        }
      `}</style>
    </Layout>
  );
}

function Section({ title, anchor, children }) {
  return (
    <section id={anchor} className="mb-7" data-testid={`efl-section-${anchor || title.replace(/\s+/g, "-").toLowerCase()}`}>
      <h2 className="font-black text-velcor-ink mb-3 tracking-tight" style={{ fontSize: 16 }}>{title}</h2>
      {children}
    </section>
  );
}

function Table({ rows }) {
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <table className="w-full text-[13px]">
        <tbody>
          {rows.map(([k, v], i) => (
            <tr key={i} className={i < rows.length - 1 ? "border-b border-slate-100" : ""}>
              <td className="px-4 py-2.5 bg-slate-50 text-slate-600 w-[55%] md:w-[60%] align-top">{k}</td>
              <td className="px-4 py-2.5 text-velcor-ink font-semibold align-top">{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
